import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiFetch, apiUpload } from "@/lib/api";
import { toastError } from "@/lib/toast-error";

interface Attachment {
  id: string;
  payAppId?: string;
  payAppType?: string;
  changeOrderId?: string;
  fileName: string;
  fileSize: number;
  mimeType: string | null;
  storagePath: string | null;
  uploadedBy: string | null;
  uploadedDate: string | null;
  createdAt: string;
}

export type { Attachment };

function attachmentKeys(payAppType: string, payAppId: string) {
  return ["attachments", payAppType, payAppId] as const;
}

export function usePayAppAttachments(payAppType: "sub" | "owner", payAppId: string) {
  const route =
    payAppType === "sub"
      ? `/pay-applications/subcontract/${payAppId}/attachments`
      : `/pay-applications/owner/${payAppId}/attachments`;

  return useQuery({
    queryKey: attachmentKeys(payAppType, payAppId),
    queryFn: () => apiFetch<Attachment[]>(route),
    enabled: !!payAppId,
  });
}

export function useUploadPayAppAttachment(payAppType: "sub" | "owner", payAppId: string) {
  const qc = useQueryClient();
  const route =
    payAppType === "sub"
      ? `/pay-applications/subcontract/${payAppId}/attachments`
      : `/pay-applications/owner/${payAppId}/attachments`;

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiUpload<unknown>(route, formData);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attachmentKeys(payAppType, payAppId) });
      toast.success("File uploaded");
    },
    onError: (err: Error) =>
      toastError(err, { action: "upload-pay-app-attachment", extra: { payAppType, payAppId } }),
  });
}

export function useDeletePayAppAttachment(payAppType: "sub" | "owner", payAppId: string) {
  const qc = useQueryClient();
  const routeBase =
    payAppType === "sub"
      ? `/pay-applications/subcontract/${payAppId}/attachments`
      : `/pay-applications/owner/${payAppId}/attachments`;

  return useMutation({
    mutationFn: (attachmentId: string) => apiDelete(`${routeBase}/${attachmentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attachmentKeys(payAppType, payAppId) });
      toast.success("Attachment deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Change Order Attachments ──

function changeOrderAttachmentKeys(changeOrderId: string) {
  return ["change-order-attachments", changeOrderId] as const;
}

export function useChangeOrderAttachments(changeOrderId: string) {
  return useQuery({
    queryKey: changeOrderAttachmentKeys(changeOrderId),
    queryFn: () => apiFetch<Attachment[]>(`/change-orders/${changeOrderId}/attachments`),
    enabled: !!changeOrderId,
  });
}

export function useUploadChangeOrderAttachment(changeOrderId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiUpload<unknown>(`/change-orders/${changeOrderId}/attachments`, formData);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: changeOrderAttachmentKeys(changeOrderId) });
      toast.success("File uploaded");
    },
    onError: (err: Error) =>
      toastError(err, { action: "upload-change-order-attachment", extra: { changeOrderId } }),
  });
}

export function useDeleteChangeOrderAttachment(changeOrderId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (attachmentId: string) =>
      apiDelete(`/change-orders/${changeOrderId}/attachments/${attachmentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: changeOrderAttachmentKeys(changeOrderId) });
      toast.success("Attachment deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
